import { buildSaldoInicialEntry } from "./saldoInicial";

/**
 * Construye el ledger contable de un proveedor (movimientos + saldo acumulado).
 *
 * Convención de signo del saldo acumulado:
 *   - saldo > 0  → el proveedor nos debe (saldo a nuestro favor)
 *   - saldo < 0  → le debemos al proveedor (deuda nuestra)
 *
 * Débitos (suben el saldo a favor nuestro):
 *   - Facturas SII recibidas (origen "SII"). NC (tipo_dte=61) entra con débito NEGATIVO.
 *   - Facturas externas no anuladas (origen "EXT") — moneda nativa.
 *   - Facturas proveedor legacy no anuladas (origen "LEG") — moneda nativa.
 *   - Cobros a cuenta del proveedor — `origen: null` (importante para el filtro de
 *     "Total facturado" en EstadoCuenta, que excluye este origen).
 *     movimientos_caja tipo='cobro' entidad_tipo='proveedor'. **Son débitos** porque
 *     representan transferencias entrantes DESDE el proveedor HACIA la empresa
 *     (préstamos del socio, devoluciones de anticipos previos, refacturaciones).
 *     Aumentan el saldo a favor nuestro: si previamente pagamos de más al proveedor
 *     (saldo<0), una devolución suya cancela ese sobrepago. Si no había deuda
 *     previa, este cobro se convierte en saldo positivo (el proveedor nos quedó
 *     debiendo esa plata). NUNCA confundir con "el proveedor está pagando una
 *     deuda con nosotros" — eso no aplica en una cuenta corriente de proveedor.
 *
 * Créditos (suben la deuda con el proveedor):
 *   - Pagos al proveedor: movimientos_caja tipo='pago' entidad_tipo='proveedor'.
 *     Concepto = "Pago a cuenta" si categoria_pago='pago_a_cuenta', si no "Pago".
 *
 * Saldo inicial opcional al 2026-01-01 entra como entry sintética (origen "INI").
 *
 * Filtrado por entidad: SIEMPRE por proveedor_id numérico, nunca por nombre.
 *
 * `saldosPorFactura` se construye en el mismo loop aplicando FIFO de pagos
 * (entries con `origen===null` y `creditoDisplay>0`) sobre las facturas SII/EXT/LEG
 * registradas hasta ese punto cronológico. Una factura cuyo saldoDisplay queda ≤ 0.01
 * se filtra al final. NCs (debito<0) no se registran. Cobros a cuenta y saldo
 * inicial no participan ni de la registración ni de la aplicación FIFO.
 *
 * @param {object} args
 * @param {number} args.provId                    ID numérico del proveedor.
 * @param {Array}  args.siiRecibidas              Facturas SII recibidas del país.
 * @param {Array}  args.facturasExternas          Facturas externas del país.
 * @param {Array}  args.facturasProveedor         Facturas legacy del país.
 * @param {Array}  args.movimientosCaja           Movimientos de caja del país desde FECHA_CORTE.
 * @param {object} [args.saldoInicial]            Saldo inicial de la entidad (o null).
 * @param {string} args.monedaPrincipal           Moneda local del país ("CLP" o "PEN").
 * @param {Function} args.toDisplayCurrency       (monto, monedaOrigen, fecha) → monto en moneda display.
 * @param {Array}  [args.vinculacionesDirectas]   Filas de movimiento_bancario_facturas (MBF), sin filtrar
 *   por proveedor. Una factura con vinculación directa a un pago vía MBF se considera 100% saldada
 *   (no entra a saldosPorFactura), y el pago vinculado NO participa del FIFO. Evita residuos fantasma
 *   por drift de TC cuando un pago en moneda local salda 1:1 una factura en otra moneda. Default: [].
 * @returns {{ entries: Array, saldosPorFactura: Map<string,{saldoDisplay,factura}> }}
 *   - `entries`: ordenadas DESC por fecha, con debitoDisplay/creditoDisplay/saldo.
 *   - `saldosPorFactura`: keyed por `"${origen}:${facturaId}"` — composite porque SII/EXT/LEG
 *     son tablas con IDs independientes que pueden colisionar en raw id. Solo facturas con
 *     saldoDisplay > 0.01 (deuda restante). `saldoDisplay` está en la moneda que produzca
 *     `toDisplayCurrency` (el consumidor sabe qué moneda pasó).
 */
export const construirLedgerProveedor = ({
  provId,
  siiRecibidas,
  facturasExternas,
  facturasProveedor,
  movimientosCaja,
  saldoInicial,
  monedaPrincipal,
  toDisplayCurrency,
  vinculacionesDirectas = [],
}) => {
  if (!provId) return [];

  const debitosSii = siiRecibidas
    .filter((f) => f.proveedor_id === provId)
    .map((f) => ({
      fecha: f.fecha_emision,
      concepto: f.tipo_dte === 61 ? "NC recibida" : "Factura",
      origen: "SII",
      referencia: f.folio ? String(f.folio) : "-",
      facturaId: f.id,
      debito: f.tipo_dte === 61 ? -Number(f.monto_total) : Number(f.monto_total),
      credito: 0,
      moneda: monedaPrincipal,
      bucket: "factura",
    }));

  const debitosExt = facturasExternas
    .filter((f) => f.proveedor_id === provId && f.estado !== "anulada")
    .map((f) => ({
      fecha: f.fecha_emision,
      concepto: f.concepto || null,
      origen: "EXT",
      referencia: f.numero_documento || "-",
      facturaId: f.id,
      debito: Number(f.monto_total),
      credito: 0,
      moneda: f.moneda || monedaPrincipal,
      bucket: "factura",
      archivo_url: f.archivo_url || null,
      periodo_mes: f.periodo_mes || null,
    }));

  const debitosLeg = facturasProveedor
    .filter((f) => f.proveedor_id === provId && f.estado !== "anulada")
    .map((f) => ({
      fecha: f.fecha_emision,
      concepto: "Factura legacy",
      origen: "LEG",
      referencia: f.numero_factura || "-",
      facturaId: f.id,
      debito: Number(f.monto_total),
      credito: 0,
      moneda: f.moneda || monedaPrincipal,
      bucket: "factura",
    }));

  // Cobros a cuenta corriente del proveedor (devolución, anticipo retornado, refacturación):
  // movimientos tipo='cobro' con entidad_tipo='proveedor'. Aumentan el saldo a favor del
  // proveedor — se contabilizan como débito en el ledger.
  const debitosCobroAcuenta = movimientosCaja
    .filter((m) => m.tipo === "cobro" && m.entidad_tipo === "proveedor" && m.entidad_id === provId)
    .map((m) => ({
      fecha: m.fecha,
      concepto: m.descripcion || "Cobro a cuenta",
      origen: null,
      referencia: m.numero_documento || "-",
      descripcion: m.descripcion || "",
      debito: Number(m.monto),
      credito: 0,
      moneda: m.moneda || monedaPrincipal,
      bucket: "credito",
    }));

  const creditos = movimientosCaja
    .filter((m) => m.tipo === "pago" && m.entidad_tipo === "proveedor" && m.entidad_id === provId)
    .map((m) => ({
      fecha: m.fecha,
      concepto: m.categoria_pago === "pago_a_cuenta" ? "Pago a cuenta" : "Pago",
      origen: null,
      referencia: m.numero_documento || "-",
      descripcion: m.descripcion || "",
      debito: 0,
      credito: Number(m.monto),
      moneda: m.moneda || monedaPrincipal,
      bucket: m.categoria_pago === "pago_credito" || m.categoria_pago === "pago_a_cuenta" ? "credito" : "factura",
      // Para detectar pagos con vinculación directa a factura (MBF) y excluirlos del FIFO.
      movimientoBancarioId: m.movimiento_bancario_id ?? null,
      // Datos para que EstadoCuenta pueda accionar sobre el pago (desvincular/eliminar).
      cajaId: m.id,
      categoriaPago: m.categoria_pago,
      tieneMBF: false, // se sobreescribe abajo si el pago tiene fila MBF
    }));

  const inicial = saldoInicial ? [{ ...buildSaldoInicialEntry(saldoInicial, monedaPrincipal), bucket: "credito" }] : [];
  const entries = [...inicial, ...debitosSii, ...debitosExt, ...debitosLeg, ...debitosCobroAcuenta, ...creditos].sort(
    (a, b) => a.fecha.localeCompare(b.fecha)
  );

  // Vinculaciones directas (MBF): una factura vinculada explícitamente a un pago está 100%
  // saldada por esa vinculación, y el pago correspondiente NO entra al pool FIFO. Además, el
  // creditoDisplay de ese pago se IGUALA a la suma de los debitoDisplay de las facturas que
  // cancela (no la conversión CLP→moneda por TC): así débito y crédito se anulan exacto y el
  // saldo global queda en cero, sin residuo cambiario fantasma. Las keys del ledger son
  // composite "ORIGEN:facturaId"; MBF mapea sii_recibida→SII, factura_externa→EXT
  // (sii_emitida es de clientes, no aplica al ledger de proveedor).
  const facturasVinculadasDirecto = new Set();
  const pagosConsumidosPorMBF = new Set();
  const facturasPorPagoMBF = new Map(); // movimiento_bancario_id → ["ORIGEN:facturaId", ...]
  for (const mbf of vinculacionesDirectas) {
    const origenKey =
      mbf.factura_tipo === "sii_recibida" ? "SII" : mbf.factura_tipo === "factura_externa" ? "EXT" : null;
    if (origenKey && mbf.factura_id != null) {
      const key = `${origenKey}:${mbf.factura_id}`;
      facturasVinculadasDirecto.add(key);
      if (mbf.movimiento_bancario_id != null) {
        const lista = facturasPorPagoMBF.get(mbf.movimiento_bancario_id) || [];
        lista.push(key);
        facturasPorPagoMBF.set(mbf.movimiento_bancario_id, lista);
      }
    }
    if (mbf.movimiento_bancario_id != null) {
      pagosConsumidosPorMBF.add(mbf.movimiento_bancario_id);
    }
  }

  // Pre-cálculo del debitoDisplay de cada factura del proveedor (composite key). Es
  // independiente del orden, así que se computa antes de la Pasada 1 para poder igualar el
  // crédito de un pago vinculado a la suma de sus facturas. Solo facturas (debito > 0; las NC
  // con debito < 0 quedan fuera, igual que en la cola FIFO).
  const debitoDisplayPorFactura = new Map();
  for (const entry of entries) {
    if (
      (entry.origen === "SII" || entry.origen === "EXT" || entry.origen === "LEG") &&
      entry.debito > 0 &&
      entry.facturaId != null
    ) {
      debitoDisplayPorFactura.set(
        `${entry.origen}:${entry.facturaId}`,
        toDisplayCurrency(entry.debito, entry.moneda, entry.fecha)
      );
    }
  }

  // Pasada 1 — enrich con saldo acumulado. Sin lógica FIFO adentro. Para un pago con
  // vinculación directa (MBF), el creditoDisplay se fuerza a la suma de los debitoDisplay de
  // las facturas que cancela, de modo que se anulen exacto contra los débitos de esas facturas.
  let saldo = 0;
  const entriesConSaldo = entries.map((entry) => {
    const debitoDisplay = toDisplayCurrency(entry.debito, entry.moneda, entry.fecha);
    let creditoDisplay = toDisplayCurrency(entry.credito, entry.moneda, entry.fecha);
    if (
      entry.origen === null &&
      entry.credito > 0 &&
      entry.movimientoBancarioId != null &&
      pagosConsumidosPorMBF.has(entry.movimientoBancarioId)
    ) {
      const keysFacturas = facturasPorPagoMBF.get(entry.movimientoBancarioId) || [];
      // Solo si al menos una de las facturas vinculadas pertenece a este proveedor (está en el
      // mapa). Si ninguna (edge: vinculación a factura de otro proveedor o sii_emitida), se
      // mantiene la conversión normal por TC.
      if (keysFacturas.some((k) => debitoDisplayPorFactura.has(k))) {
        creditoDisplay = keysFacturas.reduce((acc, k) => acc + (debitoDisplayPorFactura.get(k) ?? 0), 0);
      }
    }
    saldo += debitoDisplay - creditoDisplay;
    return { ...entry, debitoDisplay, creditoDisplay, saldo };
  });

  // Pasada 2 — registrar TODAS las facturas SII/EXT/LEG primero (ASC por fecha por el
  // sort previo). Separarlo de la aplicación FIFO evita el bug del cursor cuando un
  // pago ocurre cronológicamente ANTES de algunas facturas: en single-pass `fifoIdx`
  // avanzaría sobre una cola incompleta y las facturas posteriores quedarían sin
  // recibir crédito remanente. Composite key porque SII/EXT/LEG tienen IDs
  // independientes que pueden colisionar dentro del mismo proveedor.
  const saldosPorFactura = new Map();
  const colaFacturas = [];
  // Contadores para decidir si TODA la deuda del proveedor está cubierta por MBF (→ residuo 0).
  let totalFacturasProveedor = 0;
  let facturasExcluidasMBF = 0;
  for (const entry of entriesConSaldo) {
    if (
      (entry.origen === "SII" || entry.origen === "EXT" || entry.origen === "LEG") &&
      entry.debitoDisplay > 0 &&
      entry.facturaId != null
    ) {
      const key = `${entry.origen}:${entry.facturaId}`;
      totalFacturasProveedor++;
      // Factura con vinculación directa: saldada al 100%. No entra a la cola FIFO ni al map
      // (queda fuera de "Por pagar"). El pago pareja se excluye del FIFO en la Pasada 3.
      if (facturasVinculadasDirecto.has(key)) {
        facturasExcluidasMBF++;
        continue;
      }
      saldosPorFactura.set(key, { saldoDisplay: entry.debitoDisplay, factura: entry });
      colaFacturas.push(key);
    }
  }

  // Pasada 3 — aplicar pagos al proveedor FIFO sobre la cola completa. SOLO pagos
  // de bucket factura (origen=null + creditoDisplay > 0 + bucket!="credito"): los
  // cobros a cuenta (origen=null + debito>0) NO participan — son transferencias
  // entrantes desde el proveedor que en este sistema NO se interpretan como ofset
  // de factura específica, viven solo en el saldo global del ledger. Los pagos de
  // bucket crédito (pago_credito/pago_a_cuenta) tampoco amortizan facturas: netean
  // contra sus cobros pareja en el saldo global. Incluirlos en el FIFO comía saldo
  // de facturas reales y hacía aflorar el cobro pareja como residuo fantasma.
  // Saldo inicial (origen='INI') tampoco participa: representa deuda pre-corte.
  let fifoIdx = 0;
  for (const entry of entriesConSaldo) {
    if (entry.origen !== null || entry.creditoDisplay <= 0.01 || entry.bucket === "credito") continue;
    // Pago con vinculación directa (MBF): ya saldó su(s) factura(s) explícitamente, no se
    // reparte por FIFO (si no, comería saldo de otras facturas y dejaría residuos fantasma).
    if (entry.movimientoBancarioId != null && pagosConsumidosPorMBF.has(entry.movimientoBancarioId)) continue;
    let restante = entry.creditoDisplay;
    while (restante > 0.01 && fifoIdx < colaFacturas.length) {
      const item = saldosPorFactura.get(colaFacturas[fifoIdx]);
      if (!item || item.saldoDisplay <= 0.01) {
        fifoIdx++;
        continue;
      }
      const aplicar = Math.min(restante, item.saldoDisplay);
      item.saldoDisplay -= aplicar;
      restante -= aplicar;
      if (item.saldoDisplay <= 0.01) fifoIdx++;
    }
  }

  // Descartar facturas totalmente pagadas (saldoDisplay ≤ 0.01).
  for (const [key, item] of saldosPorFactura) {
    if (item.saldoDisplay <= 0.01) saldosPorFactura.delete(key);
  }

  // Residuo: deuda global del ledger no atribuible a ninguna factura específica.
  // Aparece cuando hay cobros a cuenta / saldo inicial que suben el saldo global
  // pero el FIFO no los aplica a facturas. Se emite como entry sintética con key
  // "RESIDUO" para que el consumidor pueda mostrarlo como "Saldo a cuenta".
  // Math.abs en saldoFinal: por convención de uso, los proveedores en "Por pagar"
  // tienen saldo > 0 (le debemos). Si fuera negativo el residuo no aplica.
  //
  // Excepción: si TODAS las facturas del proveedor están cubiertas por vinculación directa
  // (MBF), no hay deuda real — la diferencia del saldo global es puramente cambiaria (pagos
  // locales convertidos a moneda extranjera no dan exacto). En ese caso el residuo se fuerza
  // a 0 para no proyectar esa diferencia de TC como "Saldo a cuenta" pendiente. El saldo
  // global (entries) NO se toca: sigue reflejando la diferencia cambiaria en EstadoCuenta.
  const todasCubiertasPorMBF = totalFacturasProveedor > 0 && facturasExcluidasMBF === totalFacturasProveedor;
  const totalPorFactura = [...saldosPorFactura.values()].reduce((acc, item) => acc + item.saldoDisplay, 0);
  const saldoFinal = entriesConSaldo.length > 0 ? Math.abs(entriesConSaldo.at(-1).saldo) : 0;
  const residuo = todasCubiertasPorMBF ? 0 : Math.round((saldoFinal - totalPorFactura) * 100) / 100;
  if (residuo > 0.01) {
    saldosPorFactura.set("RESIDUO", {
      saldoDisplay: residuo,
      factura: {
        origen: "RESIDUO",
        concepto: "Saldo a cuenta",
        referencia: null,
        fecha: new Date().toISOString().slice(0, 10),
      },
    });
  }

  // Enriquecimiento (no afecta el cálculo): marcar entries de pago cuyo movimiento bancario
  // tiene fila MBF. EstadoCuenta lo usa para decidir Desvincular (Sección A, libera factura)
  // vs Eliminar (Sección B, solo caja+banco). Aditivo — ver pendiente 9.9.
  const mbIdsConMBF = new Set((vinculacionesDirectas || []).map((v) => v.movimiento_bancario_id));
  entriesConSaldo.forEach((e) => {
    if (e.movimientoBancarioId && mbIdsConMBF.has(e.movimientoBancarioId)) {
      e.tieneMBF = true;
    }
  });

  return { entries: entriesConSaldo.reverse(), saldosPorFactura };
};
